import React, { FC, useState, useEffect, useCallback } from "react";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { Typography, CircularProgress, Button, Box, Paper } from "@mui/material";
import { Theme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import clsx from "clsx";
import { useBranchedStory } from "../../../../curation/branched-stories/hooks/useBranchedStory";
import {
    BranchedStoryProvider,
    useBranchedStoryNavigation,
} from "../../../../curation/branched-stories/state/BranchedStoryState";
import { useSessionMode } from "../../contexts/sessionMode.context";
import { startSessionSurvey } from "../../../../../client/api/session";
import { useAuth } from "../../../../../contexts/auth.context";
import { SessionType, FacilitatorStoryPartType, MappedStoryQuestionType } from "../../../../../models/modelTypes";
import { useStory } from "../../../../../hooks/useStory";
import { StoryNavigationNode } from "common/build/api-parameters/story";
import { isAutoContinueDecisionPoint } from "common/build/utils/branchedStoryUtils";
import { verbalTheme } from "../../../../layout/themes/verbal.theme";
import { StoryQuestions } from "./StoryQuestions";
import { useUtilityStyles } from "../../../../../hooks/styles/UtilityStyles";

declare module "@mui/styles/defaultTheme" {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme {}
}

/**
 * Maps branched story question shape (StoryNavigationNode.questions[])
 * to the MappedStoryQuestionType shape that StoryQuestions expects.
 */
function mapToStoryQuestionType(questions: StoryNavigationNode["questions"]): Partial<MappedStoryQuestionType>[] {
    return (questions ?? []).map((q, i) => ({
        id: i + 1,
        mappedStoryPartId: 0,
        mappedStoryId: null,
        organisationId: null,
        order: q.order,
        category: q.category,
        question: q.question,
        explaination: q.explaination ?? null,
        hint1: q.hint1 ?? null,
        hint2: q.hint2 ?? null,
        prepGuide: q.prepGuide ?? null,
        prompt: q.prompt ?? null,
        keywords: q.keywords ?? null,
        keywordDesc: q.keywordDesc ?? null,
        RelatedQuestion: [],
    }));
}

// Props interface for content component
interface ReadBranchedStoryContentProps {
    storyId: number;
    session?: SessionType;
}

/**
 * ReadBranchedStoryContent Component
 *
 * Core presentation component for branched story reading experience.
 * Matches the linear ReadStory delivery UX (PT Serif font, drop cap,
 * responsive sizing, image splitting, one-at-a-time questions via StoryQuestions).
 */
const ReadBranchedStoryContent: FC<ReadBranchedStoryContentProps> = ({ storyId, session }) => {
    const { structure, loading, error } = useBranchedStory(storyId);
    const { navigationState, navigateToPart, navigateBack } = useBranchedStoryNavigation();
    const [currentNode, setCurrentNode] = useState<StoryNavigationNode | null>(null);
    const [currentContent, setCurrentContent] = useState<string>("");
    const [contentPart1, setContentPart1] = useState<string | null | undefined>();
    const [contentPart2, setContentPart2] = useState<string | null | undefined>();

    // Question navigation — managed locally (branched stories don't persist question progress)
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
    const [showQuestions, setShowQuestions] = useState<boolean>(false);
    const [isOnDecisionStep, setIsOnDecisionStep] = useState<boolean>(false);

    // Ref for scroll to top — same pattern as ReadStory.tsx
    const myRef = React.createRef<HTMLDivElement>();
    const scrollToTop = () => {
        myRef.current?.scrollIntoView();
    };

    // Session mode context for wizard step management (same pattern as ReadStory)
    const { submitStep, changeStep, annotationsMode, presentationMode } = useSessionMode();
    const { isAuthenticated } = useAuth();
    const { facilitatorStoryParts, getFacilitatorStoryParts } = useStory();

    // Styles matching ReadStory.tsx exactly — PT Serif, drop cap, responsive sizing
    const useStyles = makeStyles(
        createStyles({
            serif: {
                fontFamily: "'PT Serif', serif",
            },
            storyTitle: {
                fontSize: presentationMode ? "32px" : "32px",
                fontWeight: 700,
                lineHeight: 1.5,
                [verbalTheme.breakpoints.up("sm")]: {
                    fontSize: presentationMode ? "48px" : "52px",
                },
                [verbalTheme.breakpoints.up("md")]: {
                    fontSize: presentationMode ? "72px" : "64px",
                },
            },
            storyBlock: {
                fontSize: presentationMode ? "20px" : "18px",
                lineHeight: 2,
                [verbalTheme.breakpoints.up("sm")]: {
                    fontSize: presentationMode ? "22px" : "20px",
                },
                marginBottom: "1em",
                [verbalTheme.breakpoints.up("md")]: {
                    fontSize: presentationMode ? "24px" : "22px",
                },
            },
            questionsRoot: {
                padding: verbalTheme.spacing(3),
            },
            storyNavButton: {
                display: "block",
                marginBottom: verbalTheme.spacing(2),
                width: "100%",
                [verbalTheme.breakpoints.up("sm")]: {
                    width: "auto",
                },
            },
            clear: {
                overflow: "auto",
            },
            bold: {
                opacity: "1",
            },
        }),
    );

    const classes = useStyles();
    const utilityClasses = useUtilityStyles();

    // Effect to update current node when navigation changes
    useEffect(() => {
        if (structure && structure.allParts) {
            const node = structure.allParts.find((p) => p.partNumber === navigationState.currentPart);
            setCurrentNode(node || null);
        }
    }, [structure, navigationState.currentPart]);

    // Load facilitator story parts (annotations) on mount
    useEffect(() => {
        if (storyId && isAuthenticated) {
            getFacilitatorStoryParts(storyId);
        }
    }, [storyId, isAuthenticated, getFacilitatorStoryParts]);

    // Update content when part or annotation mode changes — same pattern as ReadStory.tsx
    useEffect(() => {
        if (!currentNode) {
            setCurrentContent("");
            return;
        }

        let editedContent = "";
        if (isAuthenticated && facilitatorStoryParts && facilitatorStoryParts.length > 0) {
            const facilitatorPart = facilitatorStoryParts.find(
                (fp: FacilitatorStoryPartType) => fp.MappedStoryPart?.part === currentNode.partNumber,
            );
            editedContent = facilitatorPart?.html || "";
        }

        const originalContent = currentNode.content;
        setCurrentContent(annotationsMode && !presentationMode && editedContent ? editedContent : originalContent);
    }, [currentNode, annotationsMode, presentationMode, facilitatorStoryParts, isAuthenticated]);

    // Split content for image insertion — same pattern as ReadStory.tsx
    useEffect(() => {
        const patternContentPart1 = /^.{0,200}[^\n]*\n/s;
        const patternRemoveSurroundingWhitespaces = /^\s+|\s+$/s;
        const part1 = currentContent?.match(patternContentPart1)?.[0]?.replace(patternRemoveSurroundingWhitespaces, "");
        setContentPart1(part1);
        const part2 = currentContent
            ?.replace(patternContentPart1, "")
            ?.replace(patternRemoveSurroundingWhitespaces, "");
        setContentPart2(part2);
    }, [currentContent]);

    // Scroll to top when story part changes
    useEffect(() => {
        scrollToTop();
    }, [navigationState.currentPart]);

    // Reset question state when part changes
    useEffect(() => {
        setCurrentQuestionIndex(0);
        setShowQuestions(false);
        setIsOnDecisionStep(false);
    }, [navigationState.currentPart]);

    // Complete survey handler — same pattern as ReadStory.tsx
    const completeSurvey = useCallback(async () => {
        if (session && isAuthenticated) {
            await startSessionSurvey(session.id, { isSurveyInProgress: true });
            submitStep();
        } else {
            changeStep(2);
        }
    }, [session, isAuthenticated, submitStep, changeStep]);

    // Early return for loading/error states
    if (loading || error || !structure || !currentNode) {
        return (
            <>
                {loading ? (
                    <CircularProgress />
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : (
                    !currentNode &&
                    structure && (
                        <Typography color="error">Story structure is invalid or missing entry point</Typography>
                    )
                )}
            </>
        );
    }

    const mappedQuestions = mapToStoryQuestionType(currentNode.questions);
    const hasQuestions = mappedQuestions.length > 0;
    const currentDecisionPoint =
        currentNode.decisionPoints && currentNode.decisionPoints.length > 0 ? currentNode.decisionPoints[0] : null;

    // Part counting for display
    const totalParts = structure.totalParts;
    const isFirstPart = navigationState.currentPart === 1;

    const isAutoContinue = currentDecisionPoint ? isAutoContinueDecisionPoint(currentDecisionPoint) : false;
    const isRealDecision = currentDecisionPoint && !isAutoContinue;
    // Drop cap CSS class on first part only (same logic as ReadStory)
    const dropCapClass = isFirstPart && (!annotationsMode || presentationMode) ? "c-story-text" : undefined;

    // contextOverride for StoryQuestions — manages state locally
    const contextOverride = {
        currentQuestion: currentQuestionIndex,
        changeQuestion: (index: number) => {
            setCurrentQuestionIndex(index);
        },
        toggleShowQuestions: () => setShowQuestions((prev) => !prev),
        showQuestions,
        presentationMode,
        currentPart: navigationState.currentPart,
    };

    // Called when StoryQuestions "Next" moves past the last question
    const handleLastQuestionComplete = () => {
        setIsOnDecisionStep(true);
    };

    return (
        <div ref={myRef}>
            {/* Part X of Y header — matching ReadStory */}
            <Box mb={4}>
                <Typography variant="body2" className={classes.bold}>
                    Part {currentNode.partNumber} of {totalParts}
                </Typography>
                <Typography className={clsx(classes.serif, classes.storyTitle)} component="h1">
                    {structure.title}
                </Typography>
                <Typography>By {structure.author}</Typography>
                {currentNode.partLabel && (
                    <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: "bold" }}>
                        {currentNode.partLabel}
                    </Typography>
                )}
            </Box>

            {/* Story Content — PT Serif with drop cap on first part */}
            <div key={currentNode.partNumber + "a"} className={dropCapClass}>
                <Typography
                    style={{ whiteSpace: "pre-line", marginBottom: "40px" }}
                    className={clsx(classes.serif, classes.storyBlock)}
                    dangerouslySetInnerHTML={{ __html: contentPart1 ?? "" }}
                />
            </div>

            <div key={currentNode.partNumber + "b"}>
                <Typography
                    style={{ whiteSpace: "pre-line", marginBottom: "40px" }}
                    className={clsx(classes.serif, classes.storyBlock)}
                    dangerouslySetInnerHTML={{ __html: contentPart2 ?? "" }}
                />
            </div>

            {/* Discussion Questions — using StoryQuestions component for consistent UX */}
            {hasQuestions && isAuthenticated && !presentationMode && (
                <Box mb={4} className="storyPartQuestions">
                    <Paper className={classes.questionsRoot}>
                        <StoryQuestions
                            inProgress={session?.state === "InProgress"}
                            isAtHome={false}
                            currentStoryPartsQuestions={mappedQuestions}
                            contextOverride={contextOverride}
                            onLastQuestionComplete={
                                currentDecisionPoint || (currentNode.isEndNode && hasQuestions)
                                    ? handleLastQuestionComplete
                                    : undefined
                            }
                        />
                    </Paper>
                </Box>
            )}

            {/* Decision Point — inline JSX after last question */}
            {isOnDecisionStep && isRealDecision && currentDecisionPoint && (
                <Paper sx={{ p: 3, mt: 2 }}>
                    <Typography variant="h5" gutterBottom>
                        {currentDecisionPoint.decisionText}
                    </Typography>
                    <Box display="flex" gap={2} mt={2}>
                        {currentDecisionPoint.branches.map((branch) => (
                            <Button
                                key={branch.branchLabel}
                                variant="contained"
                                fullWidth
                                onClick={() => {
                                    setCurrentQuestionIndex(0);
                                    setShowQuestions(false);
                                    setIsOnDecisionStep(false);
                                    navigateToPart(branch.targetPartNumber, branch.choiceText);
                                }}
                                sx={{ py: 2, fontSize: "1.1rem" }}
                            >
                                {branch.branchLabel}: {branch.choiceText}
                            </Button>
                        ))}
                    </Box>
                </Paper>
            )}

            {/* Bottom Navigation Buttons */}
            <Box className={classes.clear} mt={3}>
                {navigationState.visitedParts.length > 1 && (
                    <Button
                        className={clsx(utilityClasses.floatLeft, classes.storyNavButton)}
                        variant="contained"
                        size="large"
                        onClick={() => navigateBack && navigateBack()}
                    >
                        Previous Story Part
                    </Button>
                )}

                {/* Continue Button for linear parts — matching ReadStory UX */}
                {isOnDecisionStep && isAutoContinue && currentDecisionPoint && (
                    <Button
                        className={clsx(utilityClasses.floatRight, classes.storyNavButton, "storyContinueBtn")}
                        onClick={() => {
                            const branch = currentDecisionPoint.branches[0];
                            navigateToPart(branch.targetPartNumber, branch.choiceText);
                        }}
                        variant="contained"
                        size="large"
                        color="primary"
                    >
                        Continue to Part {currentDecisionPoint.branches[0].targetPartNumber}
                    </Button>
                )}

                {/* End Node — Story Complete button */}
                {currentNode.isEndNode && (!hasQuestions || isOnDecisionStep) && (
                    <Button
                        className={clsx(utilityClasses.floatRight, classes.storyNavButton, "storyCompleteBtn")}
                        onClick={completeSurvey}
                        variant="contained"
                        size="large"
                        color="primary"
                    >
                        Story Complete
                    </Button>
                )}
            </Box>
        </div>
    );
};

// Props interface for main component
interface ReadBranchedStoryProps {
    storyId: number;
    session?: SessionType;
}

/**
 * ReadBranchedStory Component
 *
 * Main wrapper component for branched story presentation mode.
 * Provides theme context and state management for the reading experience.
 *
 * @param storyId - ID of the branched story to display
 * @param session - Optional session for delivery mode
 */
export const ReadBranchedStory: FC<ReadBranchedStoryProps> = ({ storyId, session }) => {
    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={verbalTheme}>
                <BranchedStoryProvider>
                    <ReadBranchedStoryContent storyId={storyId} session={session} />
                </BranchedStoryProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

// Display name for debugging (following design guide)
ReadBranchedStory.displayName = "ReadBranchedStory";
